import { ApolloClient } from "@apollo/client";
import {
  BaseDataProvider,
  DataProviderParams,
  DataProviderResult,
} from "../base/BaseDataProvider";
import {
  GET_PUBLIC_CHALLENGES,
  GET_CHALLENGE,
  GET_CHALLENGE_FEED,
  GET_ALL_CHALLENGE_UPDATES,
} from "../queries/challenges.queries";

export class ChallengesDataProvider extends BaseDataProvider {
  async getList(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getList", params);

    try {
      const result = await this.client.query({
        query: GET_PUBLIC_CHALLENGES,
        fetchPolicy: "network-only",
      });

      const data = result.data.publicChallenges || [];
      this.logSuccess(resource, "getList", { dataLength: data.length });

      return {
        data,
        total: data.length,
      };
    } catch (error: any) {
      this.logError(resource, "getList", error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getOne", params);

    try {
      const result = await this.client.query({
        query: GET_CHALLENGE,
        variables: { id: params.id },
        fetchPolicy: "network-only",
      });

      const data = result.data.challenge;
      this.logSuccess(resource, "getOne", data);

      return { data };
    } catch (error: any) {
      this.logError(resource, "getOne", error, params);
      throw error;
    }
  }

  async create(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    // Challenges creation not implemented in admin
    console.log("Create not implemented for challenges:", params);
    return { data: { id: Date.now(), ...params.data } };
  }

  async update(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    // Challenges update not implemented in admin
    console.log("Update not implemented for challenges:", params);
    return { data: { id: params.id, ...params.data } };
  }

  async delete(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    // Challenges deletion not implemented in admin
    console.log("Delete not implemented for challenges:", params);
    return { data: { id: params.id } };
  }
}

export class ChallengeUpdatesDataProvider extends BaseDataProvider {
  async getList(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getList", params);

    try {
      console.log("🔍 [ChallengeUpdates] Making GraphQL request...");

      // Используем админский эндпоинт для получения всех challenge updates
      const result = await this.client.query({
        query: GET_ALL_CHALLENGE_UPDATES,
        variables: {
          limit: params.pagination?.perPage || 100,
          offset:
            ((params.pagination?.page || 1) - 1) *
            (params.pagination?.perPage || 100),
        },
        fetchPolicy: "network-only",
      });

      console.log("📊 [ChallengeUpdates] GraphQL response:", result);
      const data = result.data.allChallengeUpdates || [];
      console.log("📋 [ChallengeUpdates] Parsed data:", data);

      this.logSuccess(resource, "getList", { dataLength: data.length });

      return {
        data,
        total: data.length,
      };
    } catch (error: any) {
      console.error("❌ [ChallengeUpdates] Detailed error:", error);
      this.logError(resource, "getList", error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    this.logInfo(resource, "getOne", params);

    try {
      const result = await this.client.query({
        query: GET_CHALLENGE_FEED,
        fetchPolicy: "network-only",
      });

      const updates = result.data.challengeFeed || [];
      const update = updates.find((upd: any) => upd.id === params.id);

      if (!update) {
        throw new Error(`Challenge update with id ${params.id} not found`);
      }

      this.logSuccess(resource, "getOne", update);
      return { data: update };
    } catch (error: any) {
      this.logError(resource, "getOne", error, params);
      throw error;
    }
  }

  async create(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    console.log("Create not implemented for challenge updates:", params);
    return { data: { id: Date.now(), ...params.data } };
  }

  async update(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    console.log("Update not implemented for challenge updates:", params);
    return { data: { id: params.id, ...params.data } };
  }

  async delete(
    resource: string,
    params: DataProviderParams,
  ): Promise<DataProviderResult> {
    console.log("Delete not implemented for challenge updates:", params);
    return { data: { id: params.id } };
  }
}
